import { z } from 'zod';
import { uuid } from './common.js';

/**
 * Sync protocol (spec 08). Single-user data, last-write-wins, gap-free per-user
 * `seq`. Payloads are entity-shaped JSON the server stores opaquely; the client
 * owns their schema, so the server keeps them as passthrough objects.
 */

export const syncEntity = z.enum(['boat', 'route', 'track', 'settings']);
export type SyncEntity = z.infer<typeof syncEntity>;

/**
 * Device-local preferences (spec 07) synced as a single whole-document,
 * last-write-wins row per user (spec 17). Shape-only validation — the server
 * stores this opaquely, but a client can't be poisoned by a garbage push from
 * another device/client version.
 */
export const settingsPayload = z.object({
  themeMode: z.enum(['auto', 'day', 'night']),
  textScale: z.enum(['small', 'normal', 'large', 'xlarge']),
  units: z.object({
    speed: z.enum(['kn', 'kmh']),
    distance: z.enum(['nm', 'km']),
    consumption: z.enum(['l_per_nm', 'l_per_h']),
    courseRef: z.enum(['true', 'magnetic']),
  }),
  trailMode: z.enum(['off', 'short', 'medium', 'long']),
  predictorMode: z.enum(['off', 'm1', 'm3', 'm6']),
  depthOverlay: z.boolean(),
  seamarkOverlay: z.boolean(),
});
export type SettingsPayload = z.infer<typeof settingsPayload>;

/**
 * A change/row id: a client-generated uuid for boat/route/track, or the fixed
 * literal `'settings'` for the one-per-user settings document (spec 17) —
 * the server namespaces that fixed id by the authenticated `user_id`.
 */
export const changeId = z.union([uuid, z.literal('settings')]);
export type ChangeId = z.infer<typeof changeId>;

/** A local change pushed to the server. */
export const changeItem = z.object({
  entity: syncEntity,
  id: changeId,
  updatedAt: z.number().int().nonnegative(),
  deleted: z.boolean(),
  payload: z.record(z.unknown()).optional(),
});
export type ChangeItem = z.infer<typeof changeItem>;

export const pushRequest = z.object({
  changes: z.array(changeItem).max(1000),
});
export type PushRequest = z.infer<typeof pushRequest>;

export const pushResult = z.object({
  id: changeId,
  applied: z.boolean(),
  seq: z.number().int().nonnegative(),
});
export const pushResponse = z.object({
  results: z.array(pushResult),
});
export type PushResponse = z.infer<typeof pushResponse>;

/** A change the server sends down on pull. */
export const serverChange = z.object({
  entity: syncEntity,
  id: changeId,
  seq: z.number().int().nonnegative(),
  deleted: z.boolean(),
  updatedAt: z.number().int().nonnegative(),
  payload: z.record(z.unknown()).nullable(),
});
export type ServerChange = z.infer<typeof serverChange>;

export const pullResponse = z.object({
  changes: z.array(serverChange),
  nextSince: z.number().int().nonnegative(),
  more: z.boolean(),
});
export type PullResponse = z.infer<typeof pullResponse>;

export const trackPointsBody = z.object({
  buf: z.string(), // base64 of the varint-encoded polyline (spec 03)
});
export type TrackPointsBody = z.infer<typeof trackPointsBody>;
